import { hydrateSceneryAirport, isStarterSceneryIcao, resolveCoords } from "./airports";
import { isDestPackSeed } from "./destPacks";
import { DEST_OVERLAY_MAX } from "./mapMemory";
import { isCommunityScanStub, sceneryStubFromIcao } from "./sceneryScan";
import { safeTrim } from "./text";
import type { SceneryAirport, SceneryPresence } from "./types";

export type PlottableDestinations = {
  plotted: SceneryAirport[];
  skippedIcaos: string[];
  /**
   * Detect inventory + hand-added Destinations, coords from catalog or OurAirports extract.
   * Not starter seed rows, not Content.xml / catalog phantoms.
   */
  source: "inventory+hand-added+coords";
  /** True when plotted was sliced to DEST_OVERLAY_MAX / the requested max. */
  capped: boolean;
};

export type PlottableDestinationsOpts = {
  preferIcaos?: readonly string[];
  max?: number;
};

/**
 * Destinations row the map may paint without a Detect hit: hand-added only.
 * Community scan stubs ride inventory. Starter seeds and Inbox dest-pack seeds are
 * filing / generation allow-list only — not permanent map dots.
 */
export function isMapHandAddedDestination(row: Pick<SceneryAirport, "icao" | "notes">): boolean {
  const code = row.icao.trim().toUpperCase();
  if (!code) return false;
  if (isCommunityScanStub(row)) return false;
  if (isStarterSceneryIcao(code)) return false;
  if (isDestPackSeed(row)) return false;
  return true;
}

/**
 * Airports the Flight tracker / Map "Show destinations" overlay should paint.
 *
 * One source of truth with Detect: Community-folder inventory (bespoke + Marketplace under
 * Community) plus Destinations → Add. Starter seeds, Inbox dest-pack seeds, and Content.xml /
 * catalog-only ICAOs are not permanent dots — use Map Find for a one-off pin outside the scan.
 * Plot when we can resolve lat/lon. Tokens without coords stay in skippedIcaos.
 */
export function plottableDestinations(
  destinations: SceneryAirport[] = [],
  inventory: SceneryPresence[] = [],
  opts: PlottableDestinationsOpts = {},
): PlottableDestinations {
  const destByIcao = new Map<string, SceneryAirport>();
  for (const row of destinations) {
    const code = row.icao.trim().toUpperCase();
    if (code) destByIcao.set(code, row);
  }

  const byIcao = new Map<string, SceneryAirport>();
  for (const row of destinations) {
    if (!isMapHandAddedDestination(row)) continue;
    const code = row.icao.trim().toUpperCase();
    byIcao.set(code, row);
  }
  for (const row of inventory) {
    const code = row.icao.trim().toUpperCase();
    if (!code || byIcao.has(code)) continue;
    byIcao.set(code, destByIcao.get(code) ?? sceneryStubFromIcao(code));
  }

  const plotted: SceneryAirport[] = [];
  const skippedIcaos: string[] = [];
  for (const [icao, raw] of [...byIcao.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const hydrated = hydrateSceneryAirport(icao, [raw, ...destinations]) ?? raw;
    const coords = resolveCoords(icao, [hydrated, ...destinations]);
    if (!coords) {
      skippedIcaos.push(icao);
      continue;
    }
    plotted.push({
      ...hydrated,
      lat: hydrated.lat ?? coords.lat,
      lon: hydrated.lon ?? coords.lon,
    });
  }
  const max = opts.max ?? DEST_OVERLAY_MAX;
  const prefer = new Set(
    (opts.preferIcaos ?? []).map((code) => safeTrim(code).toUpperCase()).filter(Boolean),
  );
  if (plotted.length <= max) {
    return { plotted, skippedIcaos, source: "inventory+hand-added+coords", capped: false };
  }
  const preferred = plotted.filter((row) => prefer.has(row.icao.trim().toUpperCase()));
  const rest = plotted.filter((row) => !prefer.has(row.icao.trim().toUpperCase()));
  const keep = [...preferred, ...rest].slice(0, max);
  return { plotted: keep, skippedIcaos, source: "inventory+hand-added+coords", capped: true };
}

export function destinationsOverlayHint(result: PlottableDestinations, inventoryCount = 0): string {
  const skip = result.skippedIcaos.length;
  const installed = inventoryCount > 0 ? ` · ${inventoryCount} on this desk’s scan` : "";
  const leftover = skip
    ? ` · ${skip} without known coordinates (scan token, not a catalog/OurAirports ICAO)`
    : "";
  const cap = result.capped ? ` · map cap ${result.plotted.length}` : "";
  return `${result.plotted.length} plotted${cap}${installed}${leftover}`;
}

export type FiledPlanTicket = {
  originIcao?: string | null;
  destIcao?: string | null;
  originName?: string | null;
  destName?: string | null;
  divertToIcao?: string | null;
  ofpAttachment?: { originIcao?: string | null; destIcao?: string | null } | null;
};

export type FiledPlanFix = {
  role: "dep" | "arr" | "divert";
  icao: string;
  name: string;
  lat: number;
  lon: number;
  source: "ofp" | "ticket";
};

export type FiledPlanRoute = {
  originIcao: string;
  destIcao: string;
  source: "ofp" | "ticket";
};

function icaoKey(value?: string | null): string {
  return safeTrim(value).toUpperCase();
}

/** OFP pair when both ends exist; otherwise the filed ticket pair. Fail-soft. */
export function filedPlanRoute(ticket?: FiledPlanTicket | null): FiledPlanRoute | null {
  if (!ticket) return null;
  const ofpOrigin = icaoKey(ticket.ofpAttachment?.originIcao);
  const ofpDest = icaoKey(ticket.ofpAttachment?.destIcao);
  const ticketOrigin = icaoKey(ticket.originIcao);
  const ticketDest = icaoKey(ticket.destIcao);
  if (ofpOrigin && ofpDest) return { originIcao: ofpOrigin, destIcao: ofpDest, source: "ofp" };
  if (ticketOrigin && ticketDest) return { originIcao: ticketOrigin, destIcao: ticketDest, source: "ticket" };
  const origin = ofpOrigin || ticketOrigin;
  const dest = ofpDest || ticketDest;
  if (!origin && !dest) return null;
  return { originIcao: origin, destIcao: dest, source: ofpOrigin || ofpDest ? "ofp" : "ticket" };
}

/**
 * Departure / arrival markers for a filed ticket or attached OFP.
 * Skips ends we cannot resolve. Never throws on missing strings / coords.
 */
export function filedPlanFixes(
  ticket?: FiledPlanTicket | null,
  destinations: SceneryAirport[] = [],
): FiledPlanFix[] {
  const route = filedPlanRoute(ticket);
  if (!route) return [];
  const out: FiledPlanFix[] = [];
  const ends: Array<{ role: FiledPlanFix["role"]; icao: string; name: string; source: FiledPlanFix["source"] }> = [
    { role: "dep", icao: route.originIcao, name: safeTrim(ticket?.originName) || route.originIcao, source: route.source },
    { role: "arr", icao: route.destIcao, name: safeTrim(ticket?.destName) || route.destIcao, source: route.source },
  ];
  const divertIcao = icaoKey(ticket?.divertToIcao);
  if (divertIcao && divertIcao !== route.destIcao) {
    ends.push({ role: "divert", icao: divertIcao, name: divertIcao, source: "ticket" });
  }
  for (const end of ends) {
    if (!end.icao) continue;
    const coords = resolveCoords(end.icao, destinations);
    if (!coords) continue;
    out.push({
      role: end.role,
      icao: end.icao,
      name: end.name || end.icao,
      lat: coords.lat,
      lon: coords.lon,
      source: end.source,
    });
  }
  return out;
}
